// B-101 — the pure half of "Cuadre del día" and of an office's pending notes.
//
// Names no client: the figures, the movements of the account and the controls come labelled from
// the backend (`reconciliation/clients/<code>.yaml`). What lives here is how they are said: the
// kinds of a pending note, the amount a person types, and which of the day's figures is a problem.

// The backend's closed vocabulary (`office_pending_items.kind`), in the operator's words.
export const TIPOS_PENDIENTE = {
  TIMBRE_DE_MAS: "Nota timbrada de más",
  NOTA_SIN_TIMBRE: "Nota sin timbrar",
  CAUSAL_ERRADA: "Causal errada",
  TX_EN_VUELO: "Transacción en vuelo",
  MANUAL: "Manual",
};

export const ESTADOS_PENDIENTE = {
  OPEN: "Abierto",
  CLOSED: "Cerrado",
  CANCELLED: "Anulado",
};

export const ORIGENES_PENDIENTE = {
  AUTO: "Automático",
  MANUAL: "A mano",
  IMPORT: "Cargado del archivo",
};

export const tipoDePendiente = (kind) => TIPOS_PENDIENTE[kind] || kind || "—";

// Why an office has no balance in the day's total.
export const MOTIVO_FALTANTE = {
  NO_CLOSING: "sin cierre ese día",
  NO_BALANCE: "sin saldo final",
};

// The correspondent account: read off a statement, or rolled forward from the day before with the
// day's movements, the way the operator's CUENTA sheet computes it (`origenCuenta.from`). Rolled
// forward it is its own expectation, so there is no "real − esperado" to show.
export const cuentaCalculada = (dia) => Boolean(dia && dia.origenCuenta && dia.origenCuenta.from === "movements");

const SIN_CUENTA = {
  NO_PREVIOUS_ACCOUNT: "Falta el saldo de la cuenta del día anterior: procesa ese día, o carga la matriz del año.",
  MOVEMENTS_MISSING: "Falta un movimiento de la cuenta en alguna oficina: revisa los reportes del banco del día.",
  NOT_ARRIVED: "Todavía no llega el extracto de la cuenta.",
  UNREADABLE: "El extracto de la cuenta no se pudo leer.",
  NO_ROW_FOR_DAY: "El extracto de la cuenta no trae este día.",
};

/** Why the account is empty that day, in the operator's words; null when it is there. */
export function porQueSinCuenta(dia) {
  if (!dia || !dia.calculado || (dia.cuenta !== null && dia.cuenta !== undefined)) return null;
  const status = dia.origenCuenta && dia.origenCuenta.status;
  return SIN_CUENTA[status] || "No se pudo calcular el saldo de la cuenta.";
}

// The amount a person types, as the backend takes it: a sign, digits and up to two decimals, no
// thousands separator. Typed the Colombian way — "−5.274.179" or "5.830.241,50" — or the plain way.
// Null when it is not an amount, or is zero (a pending note of zero explains nothing).
export function montoEscrito(texto) {
  const crudo = String(texto ?? "").trim().replace(/[\s$]/g, "").replace(/[−–]/g, "-");
  if (!crudo) return null;
  let normal = crudo;
  if (/,\d{1,2}$/.test(crudo)) normal = crudo.replace(/\./g, "").replace(",", ".");
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(crudo)) normal = crudo.replace(/\./g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(normal)) return null;
  const valor = Number(normal);
  if (!Number.isFinite(valor) || valor === 0) return null;
  return valor.toFixed(2);
}

// What stops a new pending note from being sent, in words. Empty when it can go.
export function erroresDeNuevoPendiente({ monto, nota }) {
  const errores = [];
  if (montoEscrito(monto) === null) errores.push("Escribe el monto con su signo, por ejemplo -5.274.179.");
  if (!String(nota ?? "").trim()) errores.push("Escribe una observación.");
  return errores;
}

// Is a figure of the day off? A string amount from the backend, against a tolerance in pesos.
export const fueraDeTolerancia = (valor, tolerancia = 0) => {
  if (valor === null || valor === undefined) return false;
  const n = Number(valor);
  return Number.isFinite(n) && Math.abs(n) > tolerancia;
};

// The controls worth showing first: the ones that do not hold, then the ones that could not be
// judged (an office missing a figure), then the rest.
export const controlesOrdenados = (controles = []) => {
  const peso = (c) => ({ false: 0, null: 1 })[String(c.holds)] ?? 2;
  return [...controles].sort((a, b) => peso(a) - peso(b));
};

// ---------------------------------------------------------------------------------------------
// B-108 — what the two screens need to be comfortable every day: how long a note has been open,
// filtering the notes, moving between the days the client has, the days around one, and the day as
// an Excel table. Pure: "today" is passed in, and a date is `YYYY-MM-DD`, never a `Date` built in
// the browser's zone.

const DIA_MS = 24 * 60 * 60 * 1000;
const enUtc = (fecha) => {
  const [y, m, d] = String(fecha || "").split("T")[0].split("-").map(Number);
  return y && m && d ? Date.UTC(y, m - 1, d) : null;
};

function fechaDia(fecha) {
  const [y, m, d] = String(fecha || "").split("T")[0].split("-");
  return y && m && d ? `${d}-${m}-${y}` : "";
}

/** Whole days from one date to another; null when either is missing. */
export function diasEntre(desde, hasta) {
  const a = enUtc(desde);
  const b = enUtc(hasta);
  return a === null || b === null ? null : Math.round((b - a) / DIA_MS);
}

/** How many days a note has been open: until today while open, until it closed otherwise. */
export function diasAbierta(item, hoy) {
  if (!item) return null;
  const hasta = item.status === "OPEN" || !item.closedOn ? hoy : item.closedOn;
  const dias = diasEntre(item.openedOn, hasta);
  return dias === null ? null : Math.max(dias, 0);
}

export const textoDias = (dias) => {
  if (dias === null || dias === undefined) return "—";
  if (dias === 0) return "hoy";
  return dias === 1 ? "1 día" : `${dias} días`;
};

// Age bands of the filter: a note open more than a week is the one to chase.
export const ANTIGUEDADES = [
  { key: "", label: "Cualquier antigüedad" },
  { key: "hasta3", label: "Hasta 3 días", max: 3 },
  { key: "semana", label: "De 4 a 7 días", min: 4, max: 7 },
  { key: "mas7", label: "Más de 7 días", min: 8 },
];

// A note open more than this many days is drawn as old.
export const DIAS_VIEJA = 7;

/**
 * The notes that pass the filters. Each one is optional ("" or absent lets everything through):
 * `tipo` a kind, `estado` a status, `oficina` an office code, `antiguedad` a key of ANTIGUEDADES.
 */
export function filtrarPendientes(items, filtros = {}, hoy) {
  const banda = ANTIGUEDADES.find((a) => a.key === (filtros.antiguedad || "")) || ANTIGUEDADES[0];
  return (items || []).filter((item) => {
    if (filtros.tipo && item.kind !== filtros.tipo) return false;
    if (filtros.estado && item.status !== filtros.estado) return false;
    if (filtros.oficina && String(item.officeCode) !== String(filtros.oficina)) return false;
    if (banda.min !== undefined || banda.max !== undefined) {
      const dias = diasAbierta(item, hoy);
      if (dias === null) return false;
      if (banda.min !== undefined && dias < banda.min) return false;
      if (banda.max !== undefined && dias > banda.max) return false;
    }
    return true;
  });
}

/** The kinds and offices the notes carry, for the filters' options: only what is there. */
export function opcionesDePendientes(items) {
  const tipos = [...new Set((items || []).map((i) => i.kind).filter(Boolean))];
  const oficinas = new Map();
  (items || []).forEach((i) => {
    if (i.officeCode && !oficinas.has(String(i.officeCode))) oficinas.set(String(i.officeCode), i.officeName || "");
  });
  return {
    tipos: tipos.sort((a, b) => tipoDePendiente(a).localeCompare(tipoDePendiente(b))),
    oficinas: [...oficinas]
      .map(([codigo, nombre]) => ({ codigo, nombre }))
      .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true })),
  };
}

export const hayFiltros = (filtros = {}) => Object.values(filtros).some(Boolean);

/** The open notes per office, for the panel's mark: `{ [officeCode]: { cantidad, monto } }`. */
export function pendientesPorOficina(items) {
  const porOficina = {};
  (items || []).filter((i) => i.status === "OPEN").forEach((i) => {
    const codigo = String(i.officeCode);
    const actual = porOficina[codigo] || { cantidad: 0, monto: 0 };
    const n = Number(i.amount);
    porOficina[codigo] = { cantidad: actual.cantidad + 1, monto: actual.monto + (Number.isFinite(n) ? n : 0) };
  });
  return porOficina;
}

/**
 * The days before and after one, among the dates the client has closings on (in any order). The
 * arrows move between those, so they never land on a weekend or a day nobody worked.
 */
export function fechasVecinas(fechas, fecha) {
  const orden = [...new Set((fechas || []).filter(Boolean))].sort();
  return {
    anterior: orden.filter((f) => f < fecha).pop() || null,
    siguiente: orden.find((f) => f > fecha) || null,
  };
}

/** One day's verdict, as the header and the history say it. */
export function veredictoDelDia(dia) {
  if (!dia || !dia.calculado || dia.totalOficinas === null || dia.totalOficinas === undefined) {
    return { tono: "neutro", texto: "Sin calcular" };
  }
  const residuoMal = (dia.alertas || []).some((a) => a.key === "diferencia_no_explicada");
  return residuoMal ? { tono: "mal", texto: "No cuadra" } : { tono: "ok", texto: "Cuadra" };
}

const numero = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The day as an Excel workbook (`descargarTablas`): DIFERENCIAS CONTABLES, the notes open that day
 * with how long they have been open, the offices without a balance, the controls and, when given,
 * the days before.
 */
export function hojasDelCuadre({ dia, fecha, cliente, hoy, historial = [] }) {
  const titulo = `Cuadre del día ${fechaDia(fecha)}${cliente ? ` · ${cliente}` : ""}`;
  const pendientes = dia.pendientesAbiertos || [];
  const hojas = [
    {
      nombre: "Resultado",
      titulo,
      columnas: [{ titulo: "Concepto", ancho: 44 }, { titulo: "Valor", ancho: 20, formato: "money" }],
      filas: [
        ["Total saldo de las oficinas", numero(dia.totalOficinas)],
        ["Saldo de la cuenta del corresponsal", numero(dia.cuenta)],
        ["Diferencia", numero(dia.diferencia)],
        [`Notas pendientes abiertas (${pendientes.length})`, numero(dia.totalPendientes)],
        ["Sin explicar", numero(dia.residuo)],
      ],
    },
    {
      nombre: "Notas pendientes",
      titulo,
      columnas: [
        { titulo: "Código", ancho: 10, formato: "text" }, { titulo: "Oficina", ancho: 28 },
        { titulo: "Tipo", ancho: 24 }, { titulo: "Abierta desde", ancho: 14 },
        { titulo: "Días abierta", ancho: 12, formato: "count" }, { titulo: "Valor", ancho: 18, formato: "money" },
      ],
      filas: pendientes.map((i) => [
        String(i.officeCode), i.officeName || "", tipoDePendiente(i.kind), fechaDia(i.openedOn),
        diasAbierta({ ...i, status: "OPEN" }, hoy || fecha), numero(i.amount),
      ]),
      totales: ["Total", "", "", "", null, numero(dia.totalPendientes)],
    },
  ];
  const faltantes = dia.oficinasFaltantes || [];
  if (faltantes.length) {
    hojas.push({
      nombre: "Oficinas sin saldo",
      titulo,
      columnas: [{ titulo: "Código", ancho: 10, formato: "text" }, { titulo: "Oficina", ancho: 28 }, { titulo: "Motivo", ancho: 24 }],
      filas: faltantes.map((o) => [String(o.officeCode), o.officeName || "", MOTIVO_FALTANTE[o.reason] || o.reason || ""]),
    });
  }
  const controles = controlesOrdenados(dia.controles);
  if (controles.length) {
    hojas.push({
      nombre: "Control de totales",
      titulo,
      columnas: [
        { titulo: "Concepto", ancho: 36 }, { titulo: "Oficinas", ancho: 18, formato: "money" },
        { titulo: "Banco", ancho: 18, formato: "money" }, { titulo: "Diferencia", ancho: 18, formato: "money" },
        { titulo: "Estado", ancho: 12 },
      ],
      filas: controles.map((c) => [
        c.label, numero(c.left), numero(c.right), numero(c.difference),
        c.holds === true ? "Cuadra" : c.holds === false ? "No cuadra" : "Sin revisar",
      ]),
    });
  }
  if (historial.length) {
    hojas.push({
      nombre: "Historial",
      titulo: "Días anteriores",
      columnas: [
        { titulo: "Fecha", ancho: 12 }, { titulo: "Total saldo oficinas", ancho: 20, formato: "money" },
        { titulo: "Cuenta", ancho: 20, formato: "money" }, { titulo: "Diferencia", ancho: 18, formato: "money" },
        { titulo: "Sin explicar", ancho: 18, formato: "money" }, { titulo: "Estado", ancho: 14 },
      ],
      filas: historial.map(({ fecha: f, dia: d }) => [
        fechaDia(f), numero(d && d.totalOficinas), numero(d && d.cuenta), numero(d && d.diferencia),
        numero(d && d.residuo), d ? veredictoDelDia(d).texto : "Sin datos",
      ]),
    });
  }
  return hojas;
}

export const nombreArchivoDelCuadre = (fecha) => `Cuadre del dia ${fechaDia(fecha)}.xlsx`;
